import {
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  ClipboardDocumentListIcon,
  ComputerDesktopIcon,
  PencilSquareIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import Button from '@components/atoms/button'

type Step = {
  number: number
  title: string
  description: string
  icon: React.ReactNode
  colorClass: string
  borderClass: string
}

const STEPS: Step[] = [
  {
    number: 1,
    title: 'Start the application',
    description:
      'Enter mobile number. Verify OTP. Create your teacher profile.',
    icon: <ClipboardDocumentListIcon className="h-6 w-6" />,
    colorClass: 'text-orange',
    borderClass: 'border-orange',
  },
  {
    number: 2,
    title: 'Fill the application form',
    description:
      'Submit details once: track, grades, experience, system readiness, resume.',
    icon: <PencilSquareIcon className="h-6 w-6" />,
    colorClass: 'text-orange2-400',
    borderClass: 'border-orange2-400',
  },
  {
    number: 3,
    title: 'Take the proctored subject test',
    description: 'A test link is unlocked immediately after form submission.',
    icon: <ComputerDesktopIcon className="h-6 w-6" />,
    colorClass: 'text-blue-200',
    borderClass: 'border-blue-200',
  },
  {
    number: 4,
    title: 'Grammar and fluency readiness round',
    description: 'Grammar MCQ plus live fluency and system check.',
    icon: <ChatBubbleLeftRightIcon className="h-6 w-6" />,
    colorClass: 'text-orange',
    borderClass: 'border-orange',
  },
  {
    number: 5,
    title: 'Final 1:1 interview',
    description: 'Teaching demo, concept clarity, and problem solving.',
    icon: <UserGroupIcon className="h-6 w-6" />,
    colorClass: 'text-orange2-400',
    borderClass: 'border-orange2-400',
  },
  {
    number: 6,
    title: 'Decision and next steps',
    description:
      'Selection outcome shared. Selected candidates move to onboarding and training.',
    icon: <CheckBadgeIcon className="h-6 w-6" />,
    colorClass: 'text-blue-200',
    borderClass: 'border-blue-200',
  },
]

type Props = {
  handleBecomeTeacher: () => void
}

const ApplicationSteps = ({ handleBecomeTeacher }: Props) => (
  <div className="px-5 py-12 lg:py-16 bg-white">
    <div className="container mx-auto max-w-5xl">
      {/* Header */}
      <div className="text-center mb-10">
        <h2 className="pb-3 text-2xl text-center text-grey lg:text-4xl font-600">
          Turn teaching into a real remote career
        </h2>
        <p className="text-lg text-grey mx-auto leading-tight">
          Structured teaching, strong support, real outcomes
        </p>
        <h3 className="text-2xl text-grey mt-4">
          Step-by-step application process
        </h3>
      </div>

      {/* Steps */}
      <ul className="flex flex-col gap-4 list-none p-0">
        {STEPS.map((step) => (
          <li
            key={step.number}
            className={`rounded-xl border-t-2 ${step.borderClass} bg-white p-4 shadow-md`}
          >
            <p className={`text-lg font-600 ${step.colorClass} mb-2`}>
              <span
                className={`text-4xl font-600 ${step.colorClass} leading-none mr-2`}
              >
                {step.number}
              </span>
              {step.title}
            </p>
            <div className={`flex items-start gap-2 ${step.colorClass}`}>
              <span className="mt-1 flex-shrink-0">{step.icon}</span>
              <p className="text-base text-grey-800">{step.description}</p>
            </div>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div className="flex justify-center mt-10">
        <Button
          variant="solid"
          intent="primary"
          size="lg"
          onClick={handleBecomeTeacher}
          className="w-full sm:w-auto"
        >
          Become a teacher
        </Button>
      </div>
    </div>
  </div>
)

export default ApplicationSteps
